import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { TransactionsService } from './transactions.service';
import type { Transaction } from '../models/transaction.model';

/**
 * Seller-facing "needs your attention" count — pending offers/buy-now
 * requests, accepted-but-not-yet-fulfilled orders, and refund requests, the
 * same set account-overview.component.ts already flags as `urgent: true`.
 * Drives the persistent header badge (see HeaderComponent) so a seller can't
 * miss these by staying on a page other than Offers & Requests. Same
 * reactive-on-auth-state shape as WishlistService; refreshed proactively by
 * whichever page just resolved one of these (accept/decline/confirmFulfillment
 * /refund) so the badge updates without a full reload.
 */
@Injectable({ providedIn: 'root' })
export class SellerAlertsService {
  private readonly authService = inject(AuthService);
  private readonly transactionsService = inject(TransactionsService);

  private readonly selling = signal<Transaction[]>([]);

  /** ACCEPTED only counts as urgent while the seller hasn't confirmed their
   * side yet — once they have (sellerFulfilledAt set), they're just waiting
   * on the buyer, and there's nothing left for them to do, so keeping the
   * badge lit would just be noise. */
  readonly urgentCount = computed(
    () =>
      this.selling().filter(
        (t) =>
          t.status === 'PENDING' ||
          t.status === 'REFUND_REQUESTED' ||
          (t.status === 'ACCEPTED' && !t.sellerFulfilledAt),
      ).length,
  );

  constructor() {
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.refresh();
      } else {
        this.selling.set([]);
      }
    });
  }

  refresh(): void {
    this.transactionsService.findSelling().subscribe({
      next: (items) => this.selling.set(items),
      error: () => this.selling.set([]),
    });
  }

  /** For pages that already fetched the seller's own transactions (account
   * -selling, the order detail page) — pushes that data straight in instead
   * of firing a second, redundant `findSelling()` call. */
  setFromList(items: Transaction[]): void {
    this.selling.set(items);
  }
}
